import { ToastContainer } from 'react-toastify';
import { Outlet } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';

import { useMediaQuery, useTheme } from '@mui/material';

import AppWrapper from '@common/ui/AppWrapper';

import AppSidebar from '@common/ui/AppSidebar';
import AppSidebarItem from '@common/ui/AppSidebarItem';
import { aponoColors } from '@common/utils/colors';

import Navigation from './organisms/Sidebar';
import User from './organisms/User';

import Brand from './components/Brand';
import Logo from './components/Logo';
import { useAponoAuth } from '@common/ui/Authenticated';
import { useMemo } from 'react';
import { useFlagFixed } from '@common/utils/flags/use-flag-fixed';
import { Flags } from '@common/utils/flags/feature-flags';
import routes from '@routes';

const MINIMIZED_KEY = 'sidebar-minimized';

export default function AppLayout() {
  const [minimizedSidebar, setMinimizedSidebar] = useLocalStorage(MINIMIZED_KEY, false);
  const { isEnabled: isPersonalApiTokensEnabled } = useFlagFixed(Flags.CLIENT_PERSONAL_API_TOKENS);
  const { user } = useAponoAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const minimized = isMobile || minimizedSidebar;

  const footer = useMemo(() => {
    if (user.withUser) {
      return <User minimized={minimized} />;
    }

    if (isPersonalApiTokensEnabled) {
      return (
        <AppSidebarItem
          icon="passkey"
          minimized={minimized}
          label="Personal API Tokens"
          to={routes.PersonalApiTokens.path}
        />
      );
    }
  }, [isPersonalApiTokensEnabled, minimized, user.withUser]);

  return (
    <AppWrapper
      minimizedSidebar={minimized}
      isDarkSidebar={user.isDarkSidebar}
      sidebar={
        <AppSidebar
          header={
            user.withLogo && (
              <AppSidebarItem
                minimized={minimized}
                icon={<Logo fill={aponoColors.primary[400]} />}
                label={<Brand fill={aponoColors.white} />}
                enableAnimation
              />
            )
          }
          footer={footer}
          minimized={minimized}
          onMinimizeToggle={isMobile ? undefined : () => setMinimizedSidebar((prev) => !prev)}
        >
          <Navigation minimized={minimized} />
        </AppSidebar>
      }
    >
      <Outlet />
      <Notificator />
    </AppWrapper>
  );
}

function Notificator() {
  const theme = useTheme();
  return (
    <ToastContainer position="bottom-left" hideProgressBar theme={theme.palette.mode === 'dark' ? 'dark' : 'light'} />
  );
}
